import React, { useCallback, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import { WpPageLayout, WpPageHeader, WpPageTitle, WpPageBody } from 'Wpds/components/page-layout';
import { WpButton } from 'Wpds/components/button';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import WpAlert from 'Wpds/components/alert/WpAlert';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Spinner from 'Shared/components/Spinner';
import ManagerDashIntroModal from 'Dashboard/components/ManagerDashIntroModal/ManagerDashIntroModal';
import CumulativeActions from './Metrics/CumulativeActions/CumulativeActions';
import RelationshipCadence from './Metrics/RelationshipCadence/RelationshipCadence';
import KeyManagementActivities from './Metrics/KeyManagementActivities/KeyManagementActivities';
import TimeInMeetings from './Metrics/TimeInMeetings/TimeInMeetings';
import WorkspaceActivity from './Metrics/WorkspaceActivity/WorkspaceActivity';
import CoachingSummary from './Metrics/CoachingSummary/CoachingSummary';
import TeamGoals from './Metrics/TeamGoals/TeamGoals';

// API
import { getManagerDashboard, getManagerDashboardTableData, getMeetingTimeData } from 'Shared/api/Dashboards.api';

// Styles
import './ManagerDashboard.scss';

const DATE_RANGES = [
  {
    key: 'last_30_days',
    label: 'Last 30 days',
  },
  {
    key: 'last_60_days',
    label: 'Last 60 days',
  },
  {
    key: 'this_year',
    label: 'Year to date',
  },
];

const ManagerDashboard = () => {
  const [showAlert, toggleShowAlert] = useToggle(true);
  const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGES[0]);
  const [dashboardData, setDashboardData] = useState(null);
  const [meetingTimeData, setMeetingTimeData] = useState(null);
  const [dashboardTableData, setDashboardTableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMeetingTimeData, setLoadingMeetingTimeData] = useState(true);
  const [loadingDashboardTableData, setLoadingDashboardTableData] = useState(true);

  const { newFeatureIntroModals: showNewFeatureModal } = useFlags();

  // only show the alert if less account is created less than 60 days ago
  const { user } = useCurrentUser();
  const onboardingCompleteTime = user.onboarding.completed_at;
  const now = moment();
  const isOnboardingCompletedLessThan30Days = now.diff(moment.unix(onboardingCompleteTime), 'days') < 30;

  useEffect(() => {
    setLoading(true);
    setLoadingMeetingTimeData(true);
    setLoadingDashboardTableData(true);
    getManagerDashboardTableData({ dateRange: selectedDateRange.key }).then(response => {
      setDashboardTableData(response.data);
      setLoadingDashboardTableData(false);
    });
    getMeetingTimeData({ dateRange: selectedDateRange.key }).then(response => {
      setMeetingTimeData(response.data);
      setLoadingMeetingTimeData(false);
    });
    getManagerDashboard({ dateRange: selectedDateRange.key }).then(response => {
      setDashboardData(response.data);
      setLoading(false);
    });
  }, [selectedDateRange]);

  const handleSelectDateRange = useCallback(
    selected => {
      setSelectedDateRange(selected);
    },
    [setSelectedDateRange]
  );

  const baseClassName = 'ManagerDashboard';

  return (
    <>
      <WpPageLayout width="container">
        <WpPageHeader sticky>
          <WpPageTitle title="Manager Dashboard" />
        </WpPageHeader>
        <WpPageBody>
          <div className={baseClassName}>
            {showAlert && isOnboardingCompletedLessThan30Days && (
              <div className={`${baseClassName}__alert`}>
                <WpAlert onDismiss={toggleShowAlert}>
                  Since you&apos;ve recently joined PI Perform the data shown here is limited. We&apos;ll send you an
                  email when we&apos;ve gathered more data.
                </WpAlert>
              </div>
            )}
            {loading && <Spinner activeIf />}
            {!loading && (
              <>
                <div className={`${baseClassName}__row`}>
                  <div>
                    <span className="mr-2">Show: </span>
                    <WpDropdown>
                      <WpDropdownToggle>
                        <WpButton variant="tertiary">
                          {selectedDateRange.label}
                          <WpIcon name="angle-down" />
                        </WpButton>
                      </WpDropdownToggle>
                      <WpDropdownMenu>
                        {DATE_RANGES.map(date => {
                          return (
                            <WpDropdownMenuItem key={date.key} onClick={() => handleSelectDateRange(date)}>
                              {date.label}
                            </WpDropdownMenuItem>
                          );
                        })}
                      </WpDropdownMenu>
                    </WpDropdown>
                  </div>
                </div>

                <div className={`${baseClassName}__row`}>
                  <div className={`${baseClassName}__row__item ${baseClassName}__row__item--two-third`}>
                    <CumulativeActions teamActivity={dashboardData.team_activity} />
                  </div>
                  <div className={`${baseClassName}__row__item ${baseClassName}__row__item--one-third`}>
                    {loadingMeetingTimeData && <Spinner activeIf />}
                    {!loadingMeetingTimeData && <TimeInMeetings meetingTimeData={meetingTimeData} />}
                  </div>
                </div>
                <div className={`${baseClassName}__row`}>
                  <div className={`${baseClassName}__row__item ${baseClassName}__row__item--two-third`}>
                    <RelationshipCadence relationshipCadence={dashboardData.relationship_cadence} />
                  </div>
                  <div className={`${baseClassName}__row__item ${baseClassName}__row__item--one-third`}>
                    <KeyManagementActivities keyManagementActivities={dashboardData.key_management_activities} />
                  </div>
                </div>
                {!loadingDashboardTableData && (
                  <>
                    <div className={`${baseClassName}__row`}>
                      <div className={`${baseClassName}__row__item ${baseClassName}__row__item--full`}>
                        <WorkspaceActivity data={dashboardTableData} />
                      </div>
                    </div>
                    <div className={`${baseClassName}__row`}>
                      <div className={`${baseClassName}__row__item ${baseClassName}__row__item--one-half`}>
                        <CoachingSummary data={dashboardTableData} />
                      </div>
                      <div className={`${baseClassName}__row__item ${baseClassName}__row__item--one-half`}>
                        <TeamGoals data={dashboardTableData} />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </WpPageBody>
      </WpPageLayout>
      {showNewFeatureModal && <ManagerDashIntroModal />}
    </>
  );
};

export default ManagerDashboard;
